"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import SalifyLink from "src/lib/components/link/salify-link";
import { ComponentIconValue, resolveIconCssSize } from "src/lib/components/v2/icon-utils";
import { toAssetImages } from "src/lib/utils";

import { ReactNode } from "react";

export interface TopBarSocialLink {
  icon?: string | ComponentIconValue;
  displayText?: string;
  url?: string;
  target?: "_self" | "_blank";
}

export interface TopBarProps {
  slogan?: string | ReactNode;
  socialLinks?: TopBarSocialLink[];
}

const Root = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: theme.typography.pxToRem(13),
}));

const Inner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  minHeight: 40,
  padding: theme.spacing(0.75, 2),
  maxWidth: 1200,
  margin: "0 auto",
  flexWrap: "wrap",
}));

const SocialList = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

const TopBar = ({ slogan, socialLinks = [] }: TopBarProps) => {
  const visibleLinks = socialLinks.filter((item) => item.url || item.displayText);

  return (
    <Root>
      <Inner>
        {typeof slogan === "string" || typeof slogan === "number" ? (
          <Typography component="span" variant="body2" sx={{ color: "inherit" }}>
            {slogan || ""}
          </Typography>
        ) : (
          slogan
        )}

        {visibleLinks.length > 0 && (
          <SocialList>
            {visibleLinks.map((item, index) => {
              const label = item.displayText || item.url || "";
              const iconAsset = resolveIconCssSize(item.icon, {
                width: "18px",
                height: "18px",
              });
              const content = iconAsset.url ? (
                <Box
                  component="img"
                  src={toAssetImages(iconAsset.url)}
                  alt={label}
                  sx={{
                    width: iconAsset.width || "18px",
                    height: iconAsset.height || "18px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ) : (
                label
              );

              if (!item.url) {
                return (
                  <Typography
                    key={index}
                    component="span"
                    variant="body2"
                    sx={{ color: "inherit" }}
                  >
                    {content}
                  </Typography>
                );
              }

              return (
                <SalifyLink key={index} href={item.url} target={item.target || "_blank"}>
                  <Box component="span" sx={{ color: "inherit", textDecoration: "none" }}>
                    {content}
                  </Box>
                </SalifyLink>
              );
            })}
          </SocialList>
        )}
      </Inner>
    </Root>
  );
};

export default TopBar;
